/**
 * Expandable cause → effect view for a feed event. Shows the event's direct
 * child effects (recommendations, allocation blocks, suggested actions) loaded
 * from `GET /v1/feed/{id}`.
 *
 * Booking-update children of a `batch_ingested` root are grouped by
 * `from → to` with a nested collapsible list of the bookings in each
 * transition, so a 40-row ingest doesn't dump a flat card per booking.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatDateTimePlain } from "@/lib/weekDates";
import { useFeed } from "@/context/FeedContext";
import { SensitiveInlineText } from "@/components/common/Sensitive";
import { FormattedFeedText } from "./FormattedFeedText";
import { feedEventDisplayTitle } from "./NewBookingMailFeedDetail";
import type {
  FeedEvent,
  FeedEventDetail as FeedEventDetailData,
  MarineIncidentPayload,
  TrackingDropBooking,
  TrackingDropPayload,
  VesselDelayBooking,
  VesselDelayPayload,
} from "@/types/feed";
import {
  bookingStateChangeBookingId,
  formatStateChangeLabel,
  partitionFeedEffects,
  type StateChangeGroup,
} from "./feedStateChangeGroups";
import { FeedEventCardMeta } from "./FeedEventCardMeta";
import {
  feedEventPrimaryTypeLabel,
  formatRelativeTime,
  FEED_SEVERITY_BORDER,
} from "./feedVisuals";
import {
  isVesselDelayPayload,
  vesselDelayLegLabel,
  vesselDelayLiveVessels,
} from "./vesselDelay";
import { isTrackingDropPayload } from "./trackingDrop";
import {
  isMarineIncidentPayload,
  marineIncidentSeverityLabel,
  marineVesselClassLabel,
} from "./marineIncident";
import {
  alertBookingsHref,
  bookingFirstBookHref,
  bookingHref,
  feedEventHref,
  feedEventLinkLabel,
  recommendationSampleIds,
} from "./feedLinks";
import { feedPrivacyScope } from "./feedPrivacy";
import { FeedBookingDrillPartyLine } from "./FeedBookingDrillParties";

/** Booking chips + live-vessel note for one delayed vessel effect. */
function VesselDelayBreakdown({ payload }: { payload: VesselDelayPayload }) {
  const bookings = payload.bookings ?? [];
  // Live tracking vessels this cargo actually moved onto (when it diverged from
  // the planned one) — surfaced once as a muted note.
  const liveVessels = vesselDelayLiveVessels(payload);

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1">
        {bookings.map((booking: VesselDelayBooking) => {
          const legLabel = vesselDelayLegLabel(booking);
          return (
            <Link
              key={booking.source_id}
              to={bookingHref(booking.source_id)}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
            >
              <SensitiveInlineText text={booking.source_id} />
              <span className="text-primary/70">+{booking.delay_days}d</span>
              {legLabel && (
                <span className="text-primary/60" title="ETD delay leg">
                  · {legLabel}
                </span>
              )}
              <ArrowUpRight className="size-2.5" />
            </Link>
          );
        })}
      </div>
      {liveVessels.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Now sailing on {liveVessels.join(", ")}
        </p>
      )}
    </div>
  );
}

/** Booking chips for one tracking-drop effect, with last-seen + error note. */
function TrackingDropBreakdown({ payload }: { payload: TrackingDropPayload }) {
  const bookings = payload.bookings ?? [];

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1">
        {bookings.map((booking: TrackingDropBooking) => (
          <Link
            key={booking.source_id}
            to={bookingHref(booking.source_id)}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            <SensitiveInlineText text={booking.source_id} />
            <ArrowUpRight className="size-2.5" />
          </Link>
        ))}
      </div>
      {bookings.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Last tracked {formatRelativeTime(bookings[0].last_fetched_at)}
        </p>
      )}
    </div>
  );
}

/** Vessel / type / location detail for one marine-incident child effect. */
function MarineIncidentBreakdown({
  payload,
}: {
  payload: MarineIncidentPayload;
}) {
  const vesselClass = marineVesselClassLabel(payload.vessel_class);
  const occurred = payload.occurred_on?.trim();
  const location = payload.location?.trim();
  const imos = (payload.imos ?? []).filter((n) => Number.isFinite(n));

  const facts: string[] = [];
  if (vesselClass) facts.push(vesselClass);
  if (occurred) facts.push(occurred);
  if (location) facts.push(location);
  if (imos.length > 0) facts.push(`IMO ${imos.join(", ")}`);

  return (
    <div className="mt-1.5 flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-300">
          {marineIncidentSeverityLabel(payload.severity_tier)}
        </span>
        {facts.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {facts.join(" · ")}
          </span>
        )}
      </div>
      {payload.source_url && (
        <a
          href={payload.source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
        >
          View report
          <ArrowUpRight className="size-2.5" />
        </a>
      )}
    </div>
  );
}

/** One booking inside an expanded `from → to` transition group. */
function StateChangeBookingRow({ effect }: { effect: FeedEvent }) {
  const sourceId = bookingStateChangeBookingId(effect);
  const href = sourceId
    ? bookingHref(sourceId)
    : (feedEventHref(effect) ?? null);
  const scope = effect.entity_scope ?? {};

  return (
    <div className="flex flex-col gap-1 rounded-md bg-background/50 px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
      <span className="min-w-0 truncate text-[11px] font-medium">
        {sourceId ? (
          <>
            Booking <SensitiveInlineText text={sourceId} />
          </>
        ) : (
          <FormattedFeedText
            text={feedEventDisplayTitle(effect)}
            scope={feedPrivacyScope(effect)}
          />
        )}
      </span>
      {href && (
        <Link
          to={href}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
        >
          View booking
          <ArrowUpRight className="size-2.5" />
        </Link>
      )}
      </div>
      <FeedBookingDrillPartyLine
        agent={scope.agent}
        consignee={scope.consignee}
        className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-[10px] text-muted-foreground"
      />
    </div>
  );
}

/** Collapsible `from → to` bucket with the bookings that made that transition. */
function StateChangeGroupRow({
  group,
  defaultOpen,
}: {
  group: StateChangeGroup;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const count = group.events.length;
  const label = formatStateChangeLabel(group.fromState, group.toState);
  const typeLabel = group.events.every((e) => e.event_type === "booking_rolled")
    ? "Booking rolled"
    : "Booking update";
  const head = group.events[0];

  return (
    <div className="rounded-lg bg-background/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
      >
        <div className="min-w-0 flex-1">
          <FeedEventCardMeta
            typeLabel={typeLabel}
            severity={head.severity}
            horizon={head.time_horizon}
            statusLabel={`${count} booking${count === 1 ? "" : "s"}`}
            className="mb-1"
          />
          <p className="truncate text-xs font-medium leading-tight">{label}</p>
        </div>
        <ChevronDown
          className={cn(
            "mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-1 px-2.5 pb-2">
          {group.events.map((effect) => (
            <StateChangeBookingRow key={effect.id} effect={effect} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A single child-effect row inside the expanded detail. */
function EffectRow({ effect }: { effect: FeedEvent }) {
  const payload = effect.payload as Record<string, unknown>;
  const isAction = payload?.kind === "suggested_action";
  const isBookingFirstBook = payload?.kind === "booking_first_book";
  const vesselDelay = isVesselDelayPayload(effect.payload)
    ? effect.payload
    : null;
  const trackingDrop = isTrackingDropPayload(effect.payload)
    ? effect.payload
    : null;
  const marineIncident = isMarineIncidentPayload(effect.payload)
    ? effect.payload
    : null;
  // A per-booking first-book child links to its booking detail; otherwise fall
  // back to the generic reference-based deep link.
  const href = bookingFirstBookHref(effect.payload) ?? feedEventHref(effect);
  const linkLabel = isBookingFirstBook
    ? "Review first book"
    : feedEventLinkLabel(effect);
  // Aggregated data-quality alert children (per check) drill into the handling
  // agent's bookings.
  const agentHref = href ? null : alertBookingsHref(effect.payload);
  const sampleIds = recommendationSampleIds(effect.payload);
  const privacyScope = feedPrivacyScope(effect);

  const typeLabel = feedEventPrimaryTypeLabel(
    effect.event_type,
    null,
    effect.payload,
  );

  return (
    <div
      className={cn(
        "min-w-0 rounded-lg bg-background/40 px-2.5 py-2",
        FEED_SEVERITY_BORDER[effect.severity],
      )}
    >
      <FeedEventCardMeta
        typeLabel={typeLabel}
        severity={effect.severity}
        horizon={effect.time_horizon}
        className="mb-1"
      />
      <div className="min-w-0">
        <p className="truncate text-xs font-medium leading-tight">
          <FormattedFeedText
            text={feedEventDisplayTitle(effect)}
            scope={privacyScope}
          />
        </p>
        {effect.summary && (
          <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
            <FormattedFeedText text={effect.summary} scope={privacyScope} />
          </p>
        )}
        {isBookingFirstBook && (
          <p
            className="mt-0.5 text-[10px] text-muted-foreground"
            title={formatDateTimePlain(effect.created_at)}
          >
            Generated {formatRelativeTime(effect.created_at)}
          </p>
        )}
        {vesselDelay && <VesselDelayBreakdown payload={vesselDelay} />}
        {trackingDrop && <TrackingDropBreakdown payload={trackingDrop} />}
        {marineIncident && <MarineIncidentBreakdown payload={marineIncident} />}
        {sampleIds.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {sampleIds.map((id) => (
              <Link
                key={id}
                to={bookingHref(id)}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
              >
                <SensitiveInlineText text={id} />
                <ArrowUpRight className="size-2.5" />
              </Link>
            ))}
          </div>
        )}
        {href && sampleIds.length === 0 && (
          <Link
            to={href}
            className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            {linkLabel}
            <ArrowUpRight className="size-2.5" />
          </Link>
        )}
        {agentHref && sampleIds.length === 0 && (
          <Link
            to={agentHref}
            className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            View agent bookings
            <ArrowUpRight className="size-2.5" />
          </Link>
        )}
        {isAction && (
          <button
            type="button"
            disabled
            title="Action execution arrives in a later phase"
            className="mt-1.5 inline-flex cursor-not-allowed items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary opacity-70"
          >
            {formatAction(String(payload.action ?? "review"))}
            <ArrowRight className="size-2.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function formatAction(action: string): string {
  switch (action) {
    case "notify_consignee":
      return "Notify consignee";
    case "notify_shipper":
      return "Notify shipper";
    case "review_opportunity":
    case "map_handling_requirements":
      return "Review opportunity";
    default:
      return "Take action";
  }
}

export function FeedEventDetail({ eventId }: { eventId: string }) {
  const { getEventDetail } = useFeed();
  const [detail, setDetail] = useState<FeedEventDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getEventDetail(eventId)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((err) => {
        if (active)
          setError(err instanceof Error ? err.message : "Failed to load detail");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [eventId, getEventDetail]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading effects…
      </div>
    );
  }

  if (error) {
    return (
      <p className="px-2 py-2 text-[11px] text-rose-500">{error}</p>
    );
  }

  const children = detail?.children ?? [];
  const { stateGroups, other } = partitionFeedEffects(children);

  if (children.length === 0) {
    return (
      <p className="px-2 py-2 text-[11px] text-muted-foreground">
        No downstream effects recorded for this event.
      </p>
    );
  }

  const heading =
    stateGroups.length > 0 && other.length === 0
      ? `${children.length} booking update${children.length === 1 ? "" : "s"} · ${stateGroups.length} transition${stateGroups.length === 1 ? "" : "s"}`
      : `Caused ${children.length} effect${children.length === 1 ? "" : "s"}`;

  return (
    <div className="feed-row-detail-body">
      <p className="feed-row-detail-body__heading">{heading}</p>
      {stateGroups.map((group) => (
        <StateChangeGroupRow
          key={group.key}
          group={group}
          defaultOpen={stateGroups.length <= 3}
        />
      ))}
      {other.map((effect) => (
        <EffectRow key={effect.id} effect={effect} />
      ))}
    </div>
  );
}
